import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../App";
import ImpersonationBanner from "../components/ImpersonationBanner";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Building2, MapPin, Plus, Store, TrendingUp, FileText,
  ShoppingBag, AlertCircle, LogOut, Settings,
} from "lucide-react";
import FranchiseAddStoreDialog from "../components/FranchiseAddStoreDialog";
import FranchiseNewRequestsPanel from "../components/FranchiseNewRequestsPanel";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const APPROVAL_COLORS = {
  approved: "bg-green-500/10 text-green-600 border-green-500/30",
  pending: "bg-yellow-500/10 text-yellow-600 border-yellow-500/30",
  rejected: "bg-red-500/10 text-red-600 border-red-500/30",
  suspended: "bg-gray-500/10 text-gray-600 border-gray-500/30",
};

export default function FranchiseDashboard() {
  const { token, user, logout, impersonating, startImpersonation } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);

  const fetchData = useCallback(async () => {
    if (!token) return;
    try {
      const [statsRes, storesRes] = await Promise.all([
        axios.get(`${API}/franchise/stats`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/franchise/stores`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setStats(statsRes.data);
      setStores(storesRes.data || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // If a fresh franchise owner has no stores yet, auto-open the add-store
  // dialog so they're guided into the next step (per spec). Skipped while
  // impersonating (admin viewing read-only) since the form would 403 anyway.
  useEffect(() => {
    if (!loading && stores.length === 0 && !impersonating) {
      setAddOpen(true);
    }
  }, [loading, stores.length, impersonating]);

  const handleManageStore = async (shop_id) => {
    // While impersonating an owner, "Manage Store" should chain into a
    // read-only shop impersonation rather than the regular delegated
    // store-session (which would mint a write-access token and is blocked
    // by `block_impersonation_writes` middleware anyway). We use the
    // ORIGINAL admin token (stashed as `impersonator_token` at the start
    // of the owner impersonation) to mint a fresh shop impersonation token.
    if (impersonating) {
      try {
        const adminToken = sessionStorage.getItem("impersonator_token");
        if (!adminToken) {
          toast.error("Admin session expired — please exit impersonation and start again");
          return;
        }
        const response = await axios.post(
          `${API}/admin/impersonate-token/shop/${shop_id}`,
          {},
          { headers: { Authorization: `Bearer ${adminToken}` } },
        );
        const { access_token, user: shopUser } = response.data;
        const displayName = shopUser?.shop_name || shopUser?.email || "shop";
        // startImpersonation() preserves the existing `impersonator_token`
        // (admin) — so when the user clicks Exit, they go back to the
        // admin dashboard, not the owner view.
        startImpersonation(access_token, shopUser, "shop", displayName);
        toast.success(`Now viewing as ${displayName} (read-only)`);
        navigate("/shop/dashboard");
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Failed to open store as shop");
      }
      return;
    }

    // Regular owner flow — mint a real shop JWT and hard-reload.
    try {
      const response = await axios.post(
        `${API}/franchise/store-session/${shop_id}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      // Mirror the handleBack approach: write to sessionStorage only and
      // hard-reload. We intentionally DO NOT call `login(...)` here because
      // mutating React auth state in this tab races with ProtectedRoute —
      // ProtectedRoute on /franchise/dashboard sees userType="shop" mid-
      // flight and tries to navigate, which can clash with the hard reload
      // and dump the user at "/". Reloading from a clean sessionStorage is
      // simpler and consistent across Chrome/Safari.
      sessionStorage.setItem("franchise_owner_token", token);
      sessionStorage.setItem("franchise_session", "true");
      sessionStorage.setItem("franchise_store_name", response.data.store_name || "");
      sessionStorage.setItem("token", response.data.access_token);
      // Defer past a tick so Safari flushes the writes before navigation.
      setTimeout(() => {
        window.location.assign("/shop/dashboard");
      }, 0);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to open store");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center bg-background">
        <p className="text-muted-foreground">Loading your stores…</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-12" data-testid="franchise-dashboard">
      {/* Header */}
      <header className="border-b bg-card sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <Building2 className="h-6 w-6 text-primary shrink-0" />
            <div className="min-w-0">
              <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Multi-location</p>
              <h1 className="font-heading text-lg sm:text-xl font-semibold truncate" data-testid="franchise-business-name">
                {user?.business_name || "My Stores"}
              </h1>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <ImpersonationBanner />
            <Button variant="ghost" size="sm" onClick={() => navigate("/franchise/account")} data-testid="franchise-account-btn">
              <Settings className="h-4 w-4 mr-1" /> Account
            </Button>
            <Button variant="outline" size="sm" onClick={() => { logout(); navigate("/shop/auth"); }} data-testid="franchise-logout-btn">
              <LogOut className="h-4 w-4 mr-1" /> Log Out
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8 space-y-8">
        {/* Stats */}
        <section data-testid="franchise-stats-grid" className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <StatCard label="Active stores" value={stats?.active_stores || 0} icon={Store} testid="stat-active-stores" />
          <StatCard label="Quote requests" value={stats?.total_quote_requests || 0} icon={FileText} testid="stat-requests" />
          <StatCard label="Quotes sent" value={stats?.total_quotes_submitted || 0} icon={TrendingUp} testid="stat-quotes" />
          <StatCard label="Orders" value={stats?.total_orders || 0} icon={ShoppingBag} testid="stat-orders" />
          <StatCard label="Conversion" value={`${stats?.conversion_rate || 0}%`} icon={TrendingUp} testid="stat-conv" />
          <StatCard label="New activity" value={stats?.new_activity_count || 0} icon={AlertCircle} testid="stat-new" highlight={(stats?.new_activity_count || 0) > 0} />
        </section>

        {/* Stores section */}
        <section className="space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h2 className="font-heading text-xl font-semibold">Your stores</h2>
            <Button
              onClick={() => setAddOpen(true)}
              data-testid="add-store-btn"
              disabled={impersonating}
              title={impersonating ? "Disabled in read-only impersonation" : ""}
            >
              <Plus className="h-4 w-4 mr-1" /> Add a store
            </Button>
          </div>

          {/* New shared-request inbox — only renders rows when this
              franchise has requests matching ≥2 of its stores. */}
          {stores.length >= 2 && (
            <FranchiseNewRequestsPanel onAssigned={fetchData} />
          )}

          {stores.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center space-y-2">
                <Store className="h-10 w-10 mx-auto text-muted-foreground" />
                <p className="font-medium">No stores yet</p>
                <p className="text-sm text-muted-foreground">
                  Add your first store to start receiving quote requests in its service radius.
                </p>
                <Button onClick={() => setAddOpen(true)} data-testid="add-first-store-btn">
                  <Plus className="h-4 w-4 mr-1" /> Add your first store
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="stores-list">
              {stores.map((s) => (
                <StoreCard
                  key={s.shop_id}
                  store={s}
                  onManage={() => handleManageStore(s.shop_id)}
                  impersonating={impersonating}
                />
              ))}
            </div>
          )}
        </section>
      </main>

      <FranchiseAddStoreDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        token={token}
        onCreated={() => {
          setAddOpen(false);
          fetchData();
        }}
        canCancel={stores.length > 0}
      />
    </div>
  );
}

function StatCard({ label, value, icon: Icon, testid, highlight }) {
  return (
    <Card data-testid={testid} className={highlight ? "border-primary/50" : ""}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between gap-2">
          <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</p>
          <Icon className="h-4 w-4 text-muted-foreground" />
        </div>
        <p className={`text-2xl font-heading font-bold mt-1 ${highlight ? "text-primary" : ""}`}>{value}</p>
      </CardContent>
    </Card>
  );
}

function StoreCard({ store, onManage, impersonating }) {
  const newCount = store.new_request_count || 0;
  const disabledForApproval = store.approval_status !== "approved";
  // Even in impersonation mode, allow Manage Store — we'll chain into a
  // read-only shop impersonation. Only approval gate blocks the click.
  const disabled = disabledForApproval;
  const tooltip = disabledForApproval
    ? "Available once admin approves this store"
    : impersonating
    ? "View this store read-only (chained impersonation)"
    : "";
  return (
    <Card className="card-hover" data-testid={`store-card-${store.shop_id}`}>
      <CardContent className="p-5 space-y-3">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <h3 className="font-heading font-semibold truncate">{store.shop_name}</h3>
            {store.address?.suburb && (
              <p className="text-xs text-muted-foreground flex items-center gap-1">
                <MapPin className="h-3 w-3" />
                {store.address.suburb}{store.address.city ? `, ${store.address.city}` : ""}
              </p>
            )}
          </div>
          <Badge className={APPROVAL_COLORS[store.approval_status] || APPROVAL_COLORS.pending}>
            {store.approval_status}
          </Badge>
        </div>

        <div className="grid grid-cols-3 gap-1 text-center text-[11px] border-t pt-2">
          <div>
            <p className="text-muted-foreground">Requests</p>
            <p className="font-semibold text-sm">{newCount}</p>
          </div>
          <div>
            <p className="text-muted-foreground">Quotes</p>
            <p className="font-semibold text-sm">{store.quotes_sent || 0}</p>
          </div>
          <div>
            <p className="text-muted-foreground">Orders</p>
            <p className="font-semibold text-sm">{store.orders_won || 0}</p>
          </div>
        </div>

        {newCount > 0 && (
          <div className="text-[11px] text-primary font-medium flex items-center gap-1">
            <AlertCircle className="h-3 w-3" />
            {newCount} new {newCount === 1 ? "request" : "requests"}
          </div>
        )}

        <Button
          variant="outline"
          className="w-full"
          onClick={onManage}
          disabled={disabled}
          title={tooltip}
          data-testid={`manage-store-${store.shop_id}`}
        >
          {impersonating ? "View Store (read-only)" : "Manage Store"}
        </Button>
      </CardContent>
    </Card>
  );
}
